import liffClient from "./liffClient";

export interface PhoneBindingStatusResponse {
  isPhoneBound: boolean;
}

export interface PhoneBindingActionResponse {
  isSuccess: boolean;
  message: string | null;
  [key: string]: unknown;
}

export class PhoneBindingApiError extends Error {
  status: number;
  statusText: string;
  responseBody: string;

  constructor(
    message: string,
    status: number,
    statusText: string,
    responseBody: string,
  ) {
    super(message);

    this.name = "PhoneBindingApiError";
    this.status = status;
    this.statusText = statusText;
    this.responseBody = responseBody;
  }
}

const apiUrl = process.env.REACT_APP_LINE_API_URL;

const getAuthorizationHeaders = (): Record<string, string> => {
  const idToken = liffClient.getIdToken();

  if (!idToken) throw new Error("LINE ID token is not available.");

  return {
    accept: "text/plain",
    Authorization: `Bearer ${idToken}`,
  };
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const responseBody = await response.text();

  if (!response.ok)
    throw new PhoneBindingApiError(
      `API request failed with status ${response.status}.`,
      response.status,
      response.statusText,
      responseBody,
    );

  if (!responseBody)
    throw new PhoneBindingApiError(
      "API returned an empty response.",
      response.status,
      response.statusText,
      responseBody,
    );

  try {
    return JSON.parse(responseBody) as T;
  } catch {
    throw new PhoneBindingApiError(
      "API returned invalid JSON.",
      response.status,
      response.statusText,
      responseBody,
    );
  }
};

const getPhoneBindingStatus = async (): Promise<PhoneBindingStatusResponse> => {
  if (liffClient.isMockMode)
    return {
      isPhoneBound: false,
    };

  if (!apiUrl) throw new Error("REACT_APP_LINE_API_URL is not configured.");

  const response = await fetch(`${apiUrl}/line/user/phone-binding-status`, {
    method: "GET",
    headers: getAuthorizationHeaders(),
  });

  return parseResponse<PhoneBindingStatusResponse>(response);
};

const sendSms = async (
  phoneNumber: string,
): Promise<PhoneBindingActionResponse> => {
  if (liffClient.isMockMode)
    return {
      isSuccess: true,
      message: null,
    };

  if (!apiUrl) throw new Error("REACT_APP_LINE_API_URL is not configured.");

  const response = await fetch(`${apiUrl}/line/user/phone-binding/send-sms`, {
    method: "POST",
    headers: {
      ...getAuthorizationHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      phoneNumber,
    }),
  });

  return parseResponse<PhoneBindingActionResponse>(response);
};

const verify = async (
  phoneNumber: string,
  verificationCode: string,
): Promise<PhoneBindingActionResponse> => {
  if (liffClient.isMockMode)
    return {
      isSuccess: true,
      message: null,
    };

  if (!apiUrl) throw new Error("REACT_APP_LINE_API_URL is not configured.");

  const response = await fetch(`${apiUrl}/line/user/phone-binding/verify`, {
    method: "POST",
    headers: {
      ...getAuthorizationHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      phoneNumber,
      verificationCode,
    }),
  });

  return parseResponse<PhoneBindingActionResponse>(response);
};

const phoneBindingApi = {
  getPhoneBindingStatus,
  sendSms,
  verify,
};

export default phoneBindingApi;
